import { motion } from 'framer-motion';
import { FileText, Shield, Gavel, Mail, Phone, MapPin } from 'lucide-react';
import styled from 'styled-components';

const LegalContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const LegalContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 1rem;
    margin-top: 2rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.8;
    margin-bottom: 1rem;
  }
  
  ul {
    margin-bottom: 1rem;
    
    li {
      color: #64748b;
      line-height: 1.6;
      margin-bottom: 0.5rem;
      margin-left: 1.5rem;
    }
  }
  
  .highlight {
    color: #3b82f6;
    font-weight: 600;
  }
  
  .contact-info {
    background: #f8fafc;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-top: 2rem;
    
    .contact-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;
      color: #374151;
      
      .icon {
        color: #3b82f6;
      }
    }
  }
`;

const LegalPage = () => {
  return (
    <LegalContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>Mentions Légales</h1>
              <p>Informations légales et conditions d'utilisation d'INZA STORE</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <LegalContent>
              <h2>
                <Gavel size={24} />
                Informations générales
              </h2>
              <p>
                Conformément aux dispositions de la loi n°2013-450 du 19 juin 2013 relative à la protection des consommateurs en Côte d'Ivoire,
                nous vous informons des éléments suivants :
              </p>
              
              <h3>Éditeur du site</h3>
              <p>
                <span className="highlight">INZA STORE</span> - Société de commercialisation de produits technologiques
              </p>
              <ul>
                <li>Forme juridique : Société à Responsabilité Limitée (SARL)</li>
                <li>Capital social : 5 000 000 XOF</li>
                <li>Adresse : Abidjan, Plateau, Face à la BCEAO, Immeuble Le Concorde, 2ème étage</li>
                <li>Téléphone : +225 20 00 00 00</li>
                <li>Email : contact@inza-store.ci</li>
                <li>N° RCCM : CI-ABJ-2020-B-1234</li>
                <li>N° Contribuable : 1234567890</li>
              </ul>

              <h3>Directeur de publication</h3>
              <p>
                Le Directeur de la publication du site inza-store.ci est le Directeur Général de la société INZA STORE.
              </p>

              <h3>Hébergement</h3>
              <p>
                Ce site est hébergé par un prestataire professionnel conformément à la législation ivoirienne en vigueur.
              </p>

              <h2>
                <Shield size={24} />
                Propriété intellectuelle
              </h2>
              <p>
                L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle.
                Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques.
              </p>
              
              <h3>Contenu du site</h3>
              <p>
                La reproduction totale ou partielle de ce site, sur quelque support que ce soit, est formellement interdite sans l'autorisation expresse de INZA STORE.
              </p>

              <h3>Marques et logos</h3>
              <p>
                Apple®, iPhone®, iPad®, MacBook®, AirPods®, Apple Watch® sont des marques déposées de Apple Inc.
                INZA STORE est un revendeur agréé et indépendant des produits Apple.
              </p>

              <h2>
                <FileText size={24} />
                Conditions d'utilisation
              </h2>
              
              <h3>Objet du site</h3>
              <p>
                Le site inza-store.ci a pour objet la commercialisation de produits Apple neufs et reconditionnés en Côte d'Ivoire.
              </p>

              <h3>Prix</h3>
              <p>
                Les prix affichés sur le site sont en Francs CFA (XOF) et incluent la TVA ivoirienne.
                Ils sont fermes et non révisables pendant leur période de validité.
              </p>

              <h3>Disponibilité</h3>
              <p>
                Les produits proposés sont disponibles dans la limite des stocks disponibles.
                INZA STORE se réserve le droit de refuser une commande pour des raisons légitimes.
              </p>

              <h3>Garantie</h3>
              <p>
                Tous les produits bénéficient de la garantie légale de conformité de 2 ans en Côte d'Ivoire.
                Une garantie commerciale complémentaire peut être proposée.
              </p>

              <h2>
                <Shield size={24} />
                Protection des données personnelles
              </h2>
              <p>
                Conformément à la loi n°2013-450 du 19 juin 2013 relative à la protection des données personnelles,
                vous disposez d'un droit d'accès, de modification et de suppression des données vous concernant.
              </p>
              
              <h3>Collecte des données</h3>
              <p>
                Les informations collectées sur ce site sont destinées à INZA STORE pour la gestion de votre commande
                et peuvent être communiquées aux tiers pour l'exécution de vos commandes.
              </p>


              <h3>Cookies</h3>
              <p>
                Ce site utilise des cookies pour améliorer votre expérience de navigation.
                Vous pouvez désactiver les cookies dans les paramètres de votre navigateur.
              </p>

              <div className="contact-info">
                <h3>Contact légal</h3>
                <div className="contact-item">
                  <Mail className="icon" size={16} />
                  contact@inza-store.ci
                </div>
                <div className="contact-item">
                  <Phone className="icon" size={16} />
                  +225 20 00 00 00
                </div>
                <div className="contact-item">
                  <MapPin className="icon" size={16} />
                  Abidjan, Plateau, Face à la BCEAO
                </div>
              </div>

              <p style={{ marginTop: '2rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                Dernière mise à jour : Décembre 2024
              </p>
            </LegalContent>
          </motion.div>
        </Container>
      </Section>
    </LegalContainer>
  );
};

export default LegalPage;
